import { Spinner } from "@via/components/loading/loading.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { useCreateVoucherCode } from "@via/pages/account/admin/voucher-codes/voucher-codes.api.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import { accessCodeOptions } from "@via/utils/form-validators.tsx";
import dayjs from "dayjs";
import { type JSX, useEffect } from "react";
import { useForm } from "react-hook-form";

interface CreateAccessCodeFormValues {
  code: string;
  expiresAt: string;
}

const getDefaultExpiration = () => dayjs().add(1, "week").format("YYYY-MM-DD");
const getMinExpiration = () => dayjs().add(1, "week").format("YYYY-MM-DD");
const getMaxExpiration = () => dayjs().add(1, "year").format("YYYY-MM-DD");

export default function CreateVoucherForm(): JSX.Element {
  const {
    mutate: createVoucher,
    isPending: isCreating,
    isSuccess: isCreateSuccess,
  } = useCreateVoucherCode();

  const { control, handleSubmit, reset, formState } =
    useForm<CreateAccessCodeFormValues>({
      mode: "onChange",
      defaultValues: {
        code: "",
        expiresAt: getDefaultExpiration(),
      },
    });

  useEffect(() => {
    if (isCreateSuccess) {
      reset({ code: "", expiresAt: getDefaultExpiration() });
    }
  }, [isCreateSuccess, reset]);

  const onSubmit = (data: CreateAccessCodeFormValues) => {
    createVoucher({
      code: data.code.trim(),
      expiresAt: dayjs(data.expiresAt).endOf("day").toISOString(),
    });
  };

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        void handleSubmit(onSubmit)(e);
      }}
      className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">
          Create Access Code
        </h2>
        <p className="text-sm text-gray-500">
          Create a new access code that users can redeem to bypass the payment
          step during registration.
        </p>
      </div>

      <div className="space-y-3">
        <FormInputComponent
          control={control}
          name="code"
          label="Code"
          placeholder="Enter access code (min 5 characters)"
          validations={accessCodeOptions.code}
          showRequiredField
          className="w-96"
          maxLength={255}
        />

        <FormInputComponent
          control={control}
          name="expiresAt"
          label="Expiration Date"
          type="date"
          validations={accessCodeOptions.expiresAt}
          showRequiredField
          className="w-96"
          min={getMinExpiration()}
          max={getMaxExpiration()}
        />
        <p className="text-xs text-gray-500">
          Maximum expiration is 1 year from today. Default is 1 week.
        </p>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => reset({ code: "", expiresAt: getDefaultExpiration() })}
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
          Clear
        </button>
        <button
          type="submit"
          disabled={isCreating || !formState.isValid}
          className={classNames(
            "inline-flex justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm",
            isCreating || !formState.isValid
              ? "bg-indigo-400 cursor-not-allowed"
              : "bg-indigo-600 hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          )}>
          <Spinner loading={isCreating}>Create Access Code</Spinner>
        </button>
      </div>
    </form>
  );
}
